import { $t } from "@/i18n";

export const ACCOUNTING_BOOKING_COLUMNS = {
  POSITION: "position",
  DEBIT_ORGANIZATION_ID: "debitOrganizationId",
  DEBIT_ACCOUNT_ID: "debitAccountId",
  CREDIT_ORGANIZATION_ID: "creditOrganizationId",
  CREDIT_ACCOUNT_ID: "creditAccountId",
  DESCRIPTION: "description",
  AMOUNT: "amount",
  VAT_RATE_ID: "vatRateId",
  VAT_AMOUNT: "vatAmount",
  DATE: "date",
  REFERENCED_PROJECT_ID: "referencedProjectId",
  REFERENCED_ANLAGE_ID: "referencedAnlageId",
  REFERENCED_CONTACT_ID: "referencedContactId",
  COST_CENTER_ID: "costCenterId",
  LINKED_FILE_RECORD_IDS: "linkedFileRecordIds",
} as const;

export type AccountingBookingColumn = (typeof ACCOUNTING_BOOKING_COLUMNS)[keyof typeof ACCOUNTING_BOOKING_COLUMNS];

/* Columns marked editable for visual consistency (disabled in editor) — never a real focus/navigation target. */
export const ACCOUNTING_BOOKING_NON_NAVIGABLE_COLUMNS: AccountingBookingColumn[] = [
  ACCOUNTING_BOOKING_COLUMNS.POSITION,
  ACCOUNTING_BOOKING_COLUMNS.VAT_AMOUNT,
];

type ColumnTitleGetter = () => string;

/**
 * Default visible columns and their order for bookings table.
 * This is also the default order for visible columns.
 */
export const DEFAULT_ACCOUNTING_BOOKING_VISIBLE_COLUMNS: AccountingBookingColumn[] = [
  ACCOUNTING_BOOKING_COLUMNS.POSITION,
  ACCOUNTING_BOOKING_COLUMNS.DEBIT_ACCOUNT_ID,
  ACCOUNTING_BOOKING_COLUMNS.CREDIT_ACCOUNT_ID,
  ACCOUNTING_BOOKING_COLUMNS.AMOUNT,
  ACCOUNTING_BOOKING_COLUMNS.REFERENCED_PROJECT_ID,
  ACCOUNTING_BOOKING_COLUMNS.REFERENCED_ANLAGE_ID,
  ACCOUNTING_BOOKING_COLUMNS.COST_CENTER_ID,
];

/**
 * Default full order for bookings table (visible + hidden columns).
 * Keep it stable to ensure predictable serialization into `ColumnSetting.order`.
 */
export const DEFAULT_ACCOUNTING_BOOKING_COLUMN_ORDER: AccountingBookingColumn[] = [
  ACCOUNTING_BOOKING_COLUMNS.POSITION,
  ACCOUNTING_BOOKING_COLUMNS.DEBIT_ORGANIZATION_ID,
  ACCOUNTING_BOOKING_COLUMNS.DEBIT_ACCOUNT_ID,
  ACCOUNTING_BOOKING_COLUMNS.CREDIT_ORGANIZATION_ID,
  ACCOUNTING_BOOKING_COLUMNS.CREDIT_ACCOUNT_ID,
  ACCOUNTING_BOOKING_COLUMNS.DESCRIPTION,
  ACCOUNTING_BOOKING_COLUMNS.AMOUNT,
  ACCOUNTING_BOOKING_COLUMNS.VAT_RATE_ID,
  ACCOUNTING_BOOKING_COLUMNS.VAT_AMOUNT,
  ACCOUNTING_BOOKING_COLUMNS.DATE,
  ACCOUNTING_BOOKING_COLUMNS.REFERENCED_PROJECT_ID,
  ACCOUNTING_BOOKING_COLUMNS.REFERENCED_ANLAGE_ID,
  ACCOUNTING_BOOKING_COLUMNS.REFERENCED_CONTACT_ID,
  ACCOUNTING_BOOKING_COLUMNS.COST_CENTER_ID,
  ACCOUNTING_BOOKING_COLUMNS.LINKED_FILE_RECORD_IDS,
];

export const ACCOUNTING_BOOKING_COLUMN_DEFAULT_WIDTHS: Record<AccountingBookingColumn, number> = {
  [ACCOUNTING_BOOKING_COLUMNS.POSITION]: 88,
  [ACCOUNTING_BOOKING_COLUMNS.DEBIT_ORGANIZATION_ID]: 120,
  [ACCOUNTING_BOOKING_COLUMNS.DEBIT_ACCOUNT_ID]: 120,
  [ACCOUNTING_BOOKING_COLUMNS.CREDIT_ORGANIZATION_ID]: 120,
  [ACCOUNTING_BOOKING_COLUMNS.CREDIT_ACCOUNT_ID]: 120,
  [ACCOUNTING_BOOKING_COLUMNS.DESCRIPTION]: 286,
  [ACCOUNTING_BOOKING_COLUMNS.AMOUNT]: 146,
  [ACCOUNTING_BOOKING_COLUMNS.VAT_RATE_ID]: 110,
  [ACCOUNTING_BOOKING_COLUMNS.VAT_AMOUNT]: 100,
  [ACCOUNTING_BOOKING_COLUMNS.DATE]: 120,
  [ACCOUNTING_BOOKING_COLUMNS.REFERENCED_PROJECT_ID]: 165,
  [ACCOUNTING_BOOKING_COLUMNS.REFERENCED_ANLAGE_ID]: 165,
  [ACCOUNTING_BOOKING_COLUMNS.REFERENCED_CONTACT_ID]: 165,
  [ACCOUNTING_BOOKING_COLUMNS.COST_CENTER_ID]: 165,
  [ACCOUNTING_BOOKING_COLUMNS.LINKED_FILE_RECORD_IDS]: 250,
};

export const ACCOUNTING_BOOKING_COLUMN_TITLES: Record<AccountingBookingColumn, ColumnTitleGetter> = {
  position: () => $t("common.label.pos"),
  debitOrganizationId: () => $t("common.label.ouDebit"),
  debitAccountId: () => $t("common.label.debit"),
  creditOrganizationId: () => $t("common.label.ouCredit"),
  creditAccountId: () => $t("common.label.credit"),
  description: () => $t("common.label.text"),
  amount: () => $t("common.label.amount"),
  vatRateId: () => $t("common.label.vatRate"),
  vatAmount: () => $t("common.label.vatAmount"),
  date: () => $t("common.label.date"),
  referencedProjectId: () => $t("common.label.project"),
  referencedAnlageId: () => $t("common.label.asset"),
  referencedContactId: () => $t("common.label.contact"),
  costCenterId: () => $t("common.label.costCenter"),
  linkedFileRecordIds: () => $t("common.label.linkedFiles"),
};

export function getAccountingBookingColumnTitle(column: AccountingBookingColumn): string {
  return ACCOUNTING_BOOKING_COLUMN_TITLES[column]?.() ?? "";
}
